/** @module @liquidefy/code-editor/diff/diff-merge @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  ThreeWayMergeState,
  MergeConflict,
  MergeResolution,
  MergeResolutionStrategy,
  CodeSuggestion,
  ReviewComment,
  ReviewThread,
  ReviewState,
  ReviewThreadId,
  ReviewCommentId,
  Range,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asReviewThreadId,
  asReviewCommentId,
  now,
  emptyReviewState,
} from '../code-editor.js';

// ═══ §2 THREE-WAY MERGE — threeWayMerge, detectConflicts, autoMerge, mergeRegions ═══

export function threeWayMerge(base: string, local: string, remote: string): EditorResult<ThreeWayMergeState> {
  const baseLines = splitLines(base);
  const localLines = splitLines(local);
  const remoteLines = splitLines(remote);

  const conflicts = detectConflicts(baseLines, localLines, remoteLines);
  const autoResolutions: MergeResolution[] = [];
  let resultLines: string[];

  if (conflicts.length === 0) {
    resultLines = autoMergeLines(baseLines, localLines, remoteLines);
  } else {
    const autoResolvable = conflicts.filter(c => isAutoResolvable(c));
    for (const conflict of autoResolvable) {
      const resolution = suggestResolution(conflict);
      autoResolutions.push({
        conflictId: conflict.id,
        strategy: resolution.strategy,
        content: resolution.content,
      });
    }
    resultLines = buildMergeResult(baseLines, localLines, remoteLines, conflicts, autoResolutions);
  }

  const state: ThreeWayMergeState = {
    base,
    local,
    remote,
    result: resultLines.join('\n'),
    conflicts,
    autoResolutions,
  };
  return ok(state, 'MERGE_COMPUTED', `Three-way merge: ${conflicts.length} conflicts found, ${autoResolutions.length} auto-resolved`);
}

export function detectConflicts(
  baseLines: string[],
  localLines: string[],
  remoteLines: string[]
): MergeConflict[] {
  const conflicts: MergeConflict[] = [];
  const localChanges = findChangedRegions(baseLines, localLines);
  const remoteChanges = findChangedRegions(baseLines, remoteLines);

  for (const localChange of localChanges) {
    for (const remoteChange of remoteChanges) {
      if (regionsOverlap(localChange, remoteChange)) {
        const localContent = localLines.slice(localChange.start, localChange.end).join('\n');
        const remoteContent = remoteLines.slice(remoteChange.start, remoteChange.end).join('\n');
        const baseContent = baseLines.slice(localChange.baseStart, localChange.baseEnd).join('\n');

        if (localContent !== remoteContent) {
          conflicts.push({
            id: generateId('conflict-'),
            range: {
              start: { line: localChange.baseStart + 1, column: 0 },
              end: { line: localChange.baseEnd + 1, column: 0 },
            },
            baseContent,
            localContent,
            remoteContent,
          });
        }
      }
    }
  }
  return conflicts;
}

export function autoMerge(base: string, local: string, remote: string): EditorResult<string> {
  const result = threeWayMerge(base, local, remote);
  if (!result.success) return err(result.code, result.message, result.error);
  const state = result.value;
  if (state.conflicts.length > 0) {
    const unresolved = state.conflicts.filter(c =>
      !state.autoResolutions.find(r => r.conflictId === c.id)
    );
    if (unresolved.length > 0) {
      return err('MERGE_CONFLICTS', `${unresolved.length} unresolved conflicts remain`);
    }
  }
  return ok(state.result, 'MERGE_SUCCESS', 'Auto-merge completed successfully');
}

export function mergeRegions(
  baseLines: string[],
  localLines: string[],
  remoteLines: string[]
): Array<{ type: 'unchanged' | 'local-only' | 'remote-only' | 'both' | 'conflict'; startLine: number; endLine: number }> {
  const regions: Array<{ type: 'unchanged' | 'local-only' | 'remote-only' | 'both' | 'conflict'; startLine: number; endLine: number }> = [];
  const localChanges = findChangedRegions(baseLines, localLines);
  const remoteChanges = findChangedRegions(baseLines, remoteLines);

  const allStarts = new Set<number>();
  for (let i = 0; i < baseLines.length; i++) allStarts.add(i);

  const localSet = new Set<number>();
  for (const c of localChanges) {
    for (let i = c.baseStart; i < c.baseEnd; i++) localSet.add(i);
  }
  const remoteSet = new Set<number>();
  for (const c of remoteChanges) {
    for (let i = c.baseStart; i < c.baseEnd; i++) remoteSet.add(i);
  }

  let currentType: 'unchanged' | 'local-only' | 'remote-only' | 'both' | 'conflict' = 'unchanged';
  let regionStart = 0;

  for (let i = 0; i <= baseLines.length; i++) {
    const inLocal = localSet.has(i);
    const inRemote = remoteSet.has(i);
    let lineType: 'unchanged' | 'local-only' | 'remote-only' | 'both' | 'conflict';

    if (inLocal && inRemote) lineType = 'conflict';
    else if (inLocal) lineType = 'local-only';
    else if (inRemote) lineType = 'remote-only';
    else lineType = 'unchanged';

    if (i === baseLines.length || lineType !== currentType) {
      if (i > regionStart) {
        regions.push({ type: currentType, startLine: regionStart + 1, endLine: i });
      }
      currentType = lineType;
      regionStart = i;
    }
  }
  return regions;
}

interface ChangedRegion {
  baseStart: number;
  baseEnd: number;
  start: number;
  end: number;
}

function findChangedRegions(base: string[], modified: string[]): ChangedRegion[] {
  const regions: ChangedRegion[] = [];
  const maxLen = Math.max(base.length, modified.length);
  let i = 0;

  while (i < maxLen) {
    if (i >= base.length || i >= modified.length || base[i] !== modified[i]) {
      const start = i;
      while (i < maxLen && (i >= base.length || i >= modified.length || base[i] !== modified[i])) {
        i++;
      }
      regions.push({
        baseStart: start,
        baseEnd: Math.min(i, base.length),
        start,
        end: Math.min(i, modified.length),
      });
    } else {
      i++;
    }
  }
  return regions;
}

function regionsOverlap(a: ChangedRegion, b: ChangedRegion): boolean {
  return a.baseStart < b.baseEnd && b.baseStart < a.baseEnd;
}

function autoMergeLines(base: string[], local: string[], remote: string[]): string[] {
  const result: string[] = [];
  const maxLen = Math.max(base.length, local.length, remote.length);

  for (let i = 0; i < maxLen; i++) {
    const baseLine = i < base.length ? base[i] : undefined;
    const localLine = i < local.length ? local[i] : undefined;
    const remoteLine = i < remote.length ? remote[i] : undefined;

    if (localLine === baseLine && remoteLine !== baseLine && remoteLine !== undefined) {
      result.push(remoteLine);
    } else if (remoteLine === baseLine && localLine !== baseLine && localLine !== undefined) {
      result.push(localLine);
    } else if (localLine !== undefined) {
      result.push(localLine);
    } else if (remoteLine !== undefined) {
      result.push(remoteLine);
    }
  }
  return result;
}

function buildMergeResult(
  baseLines: string[],
  localLines: string[],
  remoteLines: string[],
  conflicts: MergeConflict[],
  resolutions: MergeResolution[]
): string[] {
  const resolutionMap = new Map(resolutions.map(r => [r.conflictId, r]));
  const conflictLineMap = new Map<number, MergeConflict>();

  for (const conflict of conflicts) {
    for (let i = conflict.range.start.line - 1; i < conflict.range.end.line - 1; i++) {
      conflictLineMap.set(i, conflict);
    }
  }

  const result: string[] = [];
  const processedConflicts = new Set<string>();
  const maxLen = Math.max(baseLines.length, localLines.length, remoteLines.length);

  for (let i = 0; i < maxLen; i++) {
    const conflict = conflictLineMap.get(i);
    if (conflict && !processedConflicts.has(conflict.id)) {
      processedConflicts.add(conflict.id);
      const resolution = resolutionMap.get(conflict.id);
      if (resolution) {
        result.push(...resolution.content.split('\n'));
      } else {
        result.push(CONFLICT_MARKER_START + ' HEAD');
        result.push(...conflict.localContent.split('\n'));
        result.push(CONFLICT_MARKER_BASE);
        result.push(...conflict.baseContent.split('\n'));
        result.push(CONFLICT_MARKER_SEPARATOR);
        result.push(...conflict.remoteContent.split('\n'));
        result.push(CONFLICT_MARKER_END + ' remote');
      }
    } else if (!conflict) {
      const baseLine = i < baseLines.length ? baseLines[i] : undefined;
      const localLine = i < localLines.length ? localLines[i] : undefined;
      const remoteLine = i < remoteLines.length ? remoteLines[i] : undefined;

      if (localLine === baseLine && remoteLine !== baseLine && remoteLine !== undefined) {
        result.push(remoteLine);
      } else if (remoteLine === baseLine && localLine !== baseLine && localLine !== undefined) {
        result.push(localLine);
      } else if (localLine !== undefined) {
        result.push(localLine);
      } else if (remoteLine !== undefined) {
        result.push(remoteLine);
      }
    }
  }
  return result;
}

function splitLines(text: string): string[] {
  if (text.length === 0) return [];
  return text.split(/\r\n|\r|\n/);
}

// ═══ §3 CONFLICT RESOLUTION — resolveConflict, resolveWithLocal, resolveWithRemote, resolveWithBoth, autoResolveAll ═══

export function resolveConflict(
  state: ThreeWayMergeState,
  conflictId: string,
  strategy: MergeResolutionStrategy,
  customContent?: string
): EditorResult<ThreeWayMergeState> {
  const conflict = state.conflicts.find(c => c.id === conflictId);
  if (!conflict) {
    return err('CONFLICT_NOT_FOUND', `Conflict "${conflictId}" not found`);
  }

  let content: string;
  switch (strategy) {
    case 'accept-local': content = conflict.localContent; break;
    case 'accept-remote': content = conflict.remoteContent; break;
    case 'accept-both': content = conflict.localContent + '\n' + conflict.remoteContent; break;
    case 'custom': content = customContent ?? conflict.localContent; break;
    default: content = conflict.localContent;
  }

  const resolution: MergeResolution = { conflictId, strategy, content };
  const updatedConflicts = state.conflicts.map(c =>
    c.id === conflictId ? { ...c, resolution: strategy } : c
  );
  const updatedResolutions = [
    ...state.autoResolutions.filter(r => r.conflictId !== conflictId),
    resolution,
  ];

  const baseLines = splitLines(state.base);
  const localLines = splitLines(state.local);
  const remoteLines = splitLines(state.remote);
  const resultLines = buildMergeResult(baseLines, localLines, remoteLines, updatedConflicts, updatedResolutions);

  return ok(
    {
      ...state,
      conflicts: updatedConflicts,
      autoResolutions: updatedResolutions,
      result: resultLines.join('\n'),
    },
    'CONFLICT_RESOLVED',
    `Conflict resolved with strategy "${strategy}"`
  );
}

export function resolveWithLocal(conflict: MergeConflict): MergeResolution {
  return { conflictId: conflict.id, strategy: 'accept-local', content: conflict.localContent };
}

export function resolveWithRemote(conflict: MergeConflict): MergeResolution {
  return { conflictId: conflict.id, strategy: 'accept-remote', content: conflict.remoteContent };
}

export function resolveWithBoth(conflict: MergeConflict, localFirst: boolean = true): MergeResolution {
  const content = localFirst
    ? conflict.localContent + '\n' + conflict.remoteContent
    : conflict.remoteContent + '\n' + conflict.localContent;
  return { conflictId: conflict.id, strategy: 'accept-both', content };
}

export function resolveWithBase(conflict: MergeConflict): MergeResolution {
  return { conflictId: conflict.id, strategy: 'custom', content: conflict.baseContent };
}

export function resolveWithCustom(conflict: MergeConflict, content: string): MergeResolution {
  return { conflictId: conflict.id, strategy: 'custom', content };
}

export function autoResolveAll(state: ThreeWayMergeState): EditorResult<ThreeWayMergeState> {
  let current = state;
  for (const conflict of state.conflicts) {
    if (isAutoResolvable(conflict) && !state.autoResolutions.find(r => r.conflictId === conflict.id)) {
      const suggestion = suggestResolution(conflict);
      const result = resolveConflict(current, conflict.id, suggestion.strategy, suggestion.content);
      if (result.success) current = result.value;
    }
  }
  return ok(current, 'AUTO_RESOLVE_COMPLETE', `Auto-resolved ${current.autoResolutions.length} conflicts`);
}

// ═══ §4 CONFLICT ANALYSIS — isAutoResolvable, conflictComplexity, suggestResolution, allResolved ═══

export function isAutoResolvable(conflict: MergeConflict): boolean {
  if (conflict.localContent === conflict.baseContent) return true;
  if (conflict.remoteContent === conflict.baseContent) return true;
  if (conflict.localContent === conflict.remoteContent) return true;
  if (conflict.localContent.trim() === '' || conflict.remoteContent.trim() === '') return true;
  return false;
}

export function conflictComplexity(conflict: MergeConflict): number {
  const localLines = conflict.localContent.split('\n').length;
  const remoteLines = conflict.remoteContent.split('\n').length;
  const baseLines = conflict.baseContent.split('\n').length;
  return localLines + remoteLines + baseLines;
}

export function suggestResolution(conflict: MergeConflict): { strategy: MergeResolutionStrategy; content: string } {
  if (conflict.localContent === conflict.baseContent) {
    return { strategy: 'accept-remote', content: conflict.remoteContent };
  }
  if (conflict.remoteContent === conflict.baseContent) {
    return { strategy: 'accept-local', content: conflict.localContent };
  }
  if (conflict.localContent === conflict.remoteContent) {
    return { strategy: 'accept-local', content: conflict.localContent };
  }
  if (conflict.localContent.trim() === '') {
    return { strategy: 'accept-remote', content: conflict.remoteContent };
  }
  if (conflict.remoteContent.trim() === '') {
    return { strategy: 'accept-local', content: conflict.localContent };
  }
  return { strategy: 'accept-local', content: conflict.localContent };
}

export function conflictOverlap(conflict: MergeConflict): number {
  const localLines = conflict.localContent.split('\n');
  const remoteLines = conflict.remoteContent.split('\n');
  const localSet = new Set(localLines);
  let common = 0;
  for (const line of remoteLines) {
    if (localSet.has(line)) common++;
  }
  const maxLen = Math.max(localLines.length, remoteLines.length);
  return maxLen > 0 ? common / maxLen : 1;
}

export function allResolved(state: ThreeWayMergeState): boolean {
  return state.conflicts.every(c =>
    c.resolution !== undefined || state.autoResolutions.some(r => r.conflictId === c.id)
  );
}

export function unresolved(state: ThreeWayMergeState): MergeConflict[] {
  const resolvedIds = new Set(state.autoResolutions.map(r => r.conflictId));
  return state.conflicts.filter(c => c.resolution === undefined && !resolvedIds.has(c.id));
}

export function resolveProgress(state: ThreeWayMergeState): number {
  if (state.conflicts.length === 0) return 1;
  const resolvedIds = new Set(state.autoResolutions.map(r => r.conflictId));
  const resolvedCount = state.conflicts.filter(c =>
    c.resolution !== undefined || resolvedIds.has(c.id)
  ).length;
  return resolvedCount / state.conflicts.length;
}

export function mergePreview(state: ThreeWayMergeState): string {
  return state.result;
}

// ═══ §5 CONFLICT MARKERS — hasConflictMarkers, parseConflictMarkers, insertConflictMarkers, removeConflictMarkers ═══

export const CONFLICT_MARKER_START = '<<<<<<<';
export const CONFLICT_MARKER_SEPARATOR = '=======';
export const CONFLICT_MARKER_BASE = '|||||||';
export const CONFLICT_MARKER_END = '>>>>>>>';

export function hasConflictMarkers(content: string): boolean {
  return (
    content.includes(CONFLICT_MARKER_START) &&
    content.includes(CONFLICT_MARKER_SEPARATOR) &&
    content.includes(CONFLICT_MARKER_END)
  );
}

export function parseConflictMarkers(
  content: string
): Array<{ ours: string; base?: string; theirs: string; startLine: number; endLine: number }> {
  const lines = content.split('\n');
  const conflicts: Array<{ ours: string; base?: string; theirs: string; startLine: number; endLine: number }> = [];
  let i = 0;

  while (i < lines.length) {
    if (lines[i]!.startsWith(CONFLICT_MARKER_START)) {
      const startLine = i;
      const oursLines: string[] = [];
      const baseLines: string[] = [];
      const theirsLines: string[] = [];
      let section: 'ours' | 'base' | 'theirs' = 'ours';
      let hasBase = false;
      i++;

      while (i < lines.length) {
        if (lines[i]!.startsWith(CONFLICT_MARKER_BASE)) {
          section = 'base';
          hasBase = true;
          i++;
        } else if (lines[i]!.startsWith(CONFLICT_MARKER_SEPARATOR)) {
          section = 'theirs';
          i++;
        } else if (lines[i]!.startsWith(CONFLICT_MARKER_END)) {
          conflicts.push({
            ours: oursLines.join('\n'),
            base: hasBase ? baseLines.join('\n') : undefined,
            theirs: theirsLines.join('\n'),
            startLine: startLine + 1,
            endLine: i + 1,
          });
          i++;
          break;
        } else {
          if (section === 'ours') oursLines.push(lines[i]!);
          else if (section === 'base') baseLines.push(lines[i]!);
          else theirsLines.push(lines[i]!);
          i++;
        }
      }
    } else {
      i++;
    }
  }
  return conflicts;
}

export function insertConflictMarkers(base: string, ours: string, theirs: string): string {
  return [
    CONFLICT_MARKER_START + ' HEAD',
    ours,
    CONFLICT_MARKER_BASE,
    base,
    CONFLICT_MARKER_SEPARATOR,
    theirs,
    CONFLICT_MARKER_END + ' remote',
  ].join('\n');
}

export function removeConflictMarkers(
  content: string,
  strategy: 'ours' | 'theirs' | 'base'
): string {
  const parsed = parseConflictMarkers(content);
  if (parsed.length === 0) return content;

  const lines = content.split('\n');
  let result = '';
  let lastEnd = 0;

  for (const conflict of parsed) {
    const beforeConflict = lines.slice(lastEnd, conflict.startLine - 1).join('\n');
    if (beforeConflict) {
      result += (result ? '\n' : '') + beforeConflict;
    }

    let replacement: string;
    switch (strategy) {
      case 'ours': replacement = conflict.ours; break;
      case 'theirs': replacement = conflict.theirs; break;
      case 'base': replacement = conflict.base ?? conflict.ours; break;
      default: replacement = conflict.ours;
    }
    result += (result ? '\n' : '') + replacement;
    lastEnd = conflict.endLine;
  }

  const remaining = lines.slice(lastEnd).join('\n');
  if (remaining) result += (result ? '\n' : '') + remaining;
  return result;
}

// ═══ §6 CODE REVIEW — createReviewState, addThread, addComment, resolveThread, reopenThread, deleteComment ═══

export function createReviewState(): ReviewState {
  return emptyReviewState();
}

export function addThread(
  state: ReviewState,
  fileUri: string,
  range: Range,
  comment: { authorId: string; body: string; suggestion?: CodeSuggestion }
): EditorResult<ReviewState> {
  const threadId = asReviewThreadId(generateId('thread-'));
  const commentId = asReviewCommentId(generateId('comment-'));

  const newComment: ReviewComment = {
    id: commentId,
    threadId,
    authorId: comment.authorId,
    body: comment.body,
    timestamp: now(),
    suggestion: comment.suggestion,
    reactions: {},
  };

  const thread: ReviewThread = {
    id: threadId,
    fileUri,
    range,
    comments: [newComment],
    status: 'open',
    createdAt: now(),
  };

  return ok(
    { ...state, threads: [...state.threads, thread] },
    'THREAD_CREATED',
    `Review thread created on ${fileUri}`
  );
}

export function addComment(
  state: ReviewState,
  threadId: ReviewThreadId,
  authorId: string,
  body: string,
  suggestion?: CodeSuggestion
): EditorResult<ReviewState> {
  const threadIdx = state.threads.findIndex(t => t.id === threadId);
  if (threadIdx === -1) {
    return err('THREAD_NOT_FOUND', `Thread "${threadId}" not found`);
  }

  const commentId = asReviewCommentId(generateId('comment-'));
  const newComment: ReviewComment = {
    id: commentId,
    threadId,
    authorId,
    body,
    timestamp: now(),
    suggestion,
    reactions: {},
  };

  const existingThread = state.threads[threadIdx]!;
  const newThreads = [
    ...state.threads.slice(0, threadIdx),
    { ...existingThread, comments: [...existingThread.comments, newComment] },
    ...state.threads.slice(threadIdx + 1),
  ];

  return ok(
    { ...state, threads: newThreads },
    'COMMENT_ADDED',
    `Comment added to thread "${threadId}"`
  );
}

export function resolveThread(
  state: ReviewState,
  threadId: ReviewThreadId
): EditorResult<ReviewState> {
  const threadIdx = state.threads.findIndex(t => t.id === threadId);
  if (threadIdx === -1) {
    return err('THREAD_NOT_FOUND', `Thread "${threadId}" not found`);
  }

  const newThreads = [
    ...state.threads.slice(0, threadIdx),
    { ...state.threads[threadIdx]!, status: 'resolved' as const },
    ...state.threads.slice(threadIdx + 1),
  ];

  return ok(
    { ...state, threads: newThreads },
    'THREAD_RESOLVED',
    `Thread "${threadId}" resolved`
  );
}

export function reopenThread(
  state: ReviewState,
  threadId: ReviewThreadId
): EditorResult<ReviewState> {
  const threadIdx = state.threads.findIndex(t => t.id === threadId);
  if (threadIdx === -1) {
    return err('THREAD_NOT_FOUND', `Thread "${threadId}" not found`);
  }

  const newThreads = [
    ...state.threads.slice(0, threadIdx),
    { ...state.threads[threadIdx]!, status: 'open' as const },
    ...state.threads.slice(threadIdx + 1),
  ];

  return ok(
    { ...state, threads: newThreads },
    'THREAD_REOPENED',
    `Thread "${threadId}" reopened`
  );
}

export function deleteComment(
  state: ReviewState,
  threadId: ReviewThreadId,
  commentId: ReviewCommentId
): EditorResult<ReviewState> {
  const threadIdx = state.threads.findIndex(t => t.id === threadId);
  if (threadIdx === -1) {
    return err('THREAD_NOT_FOUND', `Thread "${threadId}" not found`);
  }

  const thread = state.threads[threadIdx];
  const commentIdx = thread!.comments.findIndex(c => c.id === commentId);
  if (commentIdx === -1) {
    return err('COMMENT_NOT_FOUND', `Comment "${commentId}" not found`);
  }

  if (thread!.comments.length === 1) {
    const newThreads = state.threads.filter(t => t.id !== threadId);
    return ok(
      { ...state, threads: newThreads },
      'THREAD_DELETED',
      `Thread "${threadId}" deleted (last comment removed)`
    );
  }

  const newComments = thread!.comments.filter(c => c.id !== commentId);
  const newThreads = [...state.threads];
  newThreads[threadIdx] = { ...thread!, comments: newComments };

  return ok(
    { ...state, threads: newThreads },
    'COMMENT_DELETED',
    `Comment "${commentId}" deleted`
  );
}

// ═══ §7 CODE SUGGESTIONS — createSuggestion, applySuggestion, previewSuggestion, isSuggestionApplied, suggestionDiff ═══

export function createSuggestion(originalCode: string, suggestedCode: string): CodeSuggestion {
  return {
    originalCode,
    suggestedCode,
    isApplied: false,
  };
}

export function applySuggestion(content: string, suggestion: CodeSuggestion): EditorResult<string> {
  // NOTE: Uses indexOf to replace only the first occurrence of originalCode.
  // For multi-occurrence precision, prefer range-based replacement via ReviewSuggestion.range in diff/review.ts.
  const idx = content.indexOf(suggestion.originalCode);
  if (idx === -1) {
    return err('SUGGESTION_NOT_APPLICABLE', 'Original code not found in content');
  }
  const newContent =
    content.substring(0, idx) +
    suggestion.suggestedCode +
    content.substring(idx + suggestion.originalCode.length);
  return ok(newContent, 'SUGGESTION_APPLIED', 'Code suggestion applied');
}

export function previewSuggestion(content: string, suggestion: CodeSuggestion): EditorResult<string> {
  return applySuggestion(content, suggestion);
}

export function isSuggestionApplied(suggestion: CodeSuggestion): boolean {
  return suggestion.isApplied;
}

export function suggestionDiff(suggestion: CodeSuggestion): { removed: string; added: string } {
  return {
    removed: suggestion.originalCode,
    added: suggestion.suggestedCode,
  };
}

// ═══ §8 REVIEW ANALYSIS — reviewSummary, pendingThreads, resolvedThreads, computeReviewStatus ═══

export function reviewSummary(state: ReviewState): {
  threads: number;
  resolved: number;
  pending: number;
  comments: number;
} {
  const resolved = state.threads.filter(t => t.status === 'resolved').length;
  const pending = state.threads.filter(t => t.status === 'open').length;
  const comments = state.threads.reduce((sum, t) => sum + t.comments.length, 0);
  return {
    threads: state.threads.length,
    resolved,
    pending,
    comments,
  };
}

export function pendingThreads(state: ReviewState): ReviewThread[] {
  return state.threads.filter(t => t.status === 'open');
}

export function resolvedThreads(state: ReviewState): ReviewThread[] {
  return state.threads.filter(t => t.status === 'resolved');
}

export function outdatedThreads(state: ReviewState): ReviewThread[] {
  return state.threads.filter(t => t.status === 'outdated');
}

export function commentsByAuthor(state: ReviewState): Map<string, ReviewComment[]> {
  const result = new Map<string, ReviewComment[]>();
  for (const thread of state.threads) {
    for (const comment of thread.comments) {
      const existing = result.get(comment.authorId) ?? [];
      existing.push(comment);
      result.set(comment.authorId, existing);
    }
  }
  return result;
}

export function computeReviewStatus(state: ReviewState): ReviewState['status'] {
  if (state.threads.length === 0) return 'draft';
  const allResolved = state.threads.every(t => t.status === 'resolved');
  const hasOpenThreads = state.threads.some(t => t.status === 'open');

  if (allResolved) return 'approved';
  if (hasOpenThreads) return 'changes-requested';
  return 'pending';
}

export function isApproved(state: ReviewState): boolean {
  return state.status === 'approved' || computeReviewStatus(state) === 'approved';
}

export function hasChangesRequested(state: ReviewState): boolean {
  return state.status === 'changes-requested' || computeReviewStatus(state) === 'changes-requested';
}

export function formatReviewSummary(state: ReviewState): string {
  const summary = reviewSummary(state);
  const parts: string[] = [];
  parts.push(`Status: ${state.status}`);
  parts.push(`${summary.threads} thread${summary.threads !== 1 ? 's' : ''}`);
  parts.push(`${summary.resolved} resolved`);
  parts.push(`${summary.pending} pending`);
  parts.push(`${summary.comments} total comment${summary.comments !== 1 ? 's' : ''}`);
  return parts.join(' | ');
}
